// Centralised display helpers for post data — used across Marketplace, Listing
// Detail, PublicListing, Dashboard, Orders, Responses so pricing / descriptions
// render consistently everywhere.

const PRICING_LABELS = {
  hour: "Hour",
  day: "Day",
  week: "Week",
  month: "Month",
  acre: "Acre",
  trip: "Trip",
  quantity: "Quantity",
  fixed: "Fixed Price",
  negotiable: "Negotiable",
  free: "Free",
};

/**
 * Format a price with the correct pricing unit suffix.
 * Examples:
 *   { price: 2000, pricing_type: "day" } -> "₹2,000 / Day"
 *   { price: 500,  pricing_type: "hour" } -> "₹500 / Hour"
 *   { price: 10000, pricing_type: "fixed" } -> "₹10,000 Fixed Price"
 *   { price: 50,   pricing_type: "quantity", unit: "kg" } -> "₹50 / kg"
 */
export function formatPriceText(item) {
  if (!item) return "";
  const pt = (item.pricing_type || "").toLowerCase();
  if (pt === "free") return "Free";
  if (pt === "negotiable") return "Negotiable";
  const price = Number(item.price || 0);
  const money = `₹${price.toLocaleString("en-IN")}`;
  if (pt === "fixed") return `${money} Fixed Price`;
  // For "quantity" prefer the real unit (kg, bag, piece) if set.
  if (pt === "quantity" && item.unit) return `${money} / ${item.unit}`;
  const label = PRICING_LABELS[pt] || (item.unit ? item.unit : "");
  return label ? `${money} / ${label}` : money;
}

/**
 * Split a description into clean sections for rendering.
 * - Preserves user line breaks
 * - Detects "Label: value" lines and turns them into section rows
 * - Detects bullets (•, -, *, 1.) and renders them as a list
 * Returns an array of { kind: "kv"|"bullet"|"para"|"spacer", ... }
 */
export function parseDescriptionBlocks(text) {
  if (!text || !text.trim()) return [];
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks = [];
  let paraBuf = [];
  let bulletBuf = [];

  const flushPara = () => {
    if (paraBuf.length) {
      blocks.push({ kind: "para", text: paraBuf.join(" ").trim() });
      paraBuf = [];
    }
  };
  const flushBullets = () => {
    if (bulletBuf.length) {
      blocks.push({ kind: "bullet", items: [...bulletBuf] });
      bulletBuf = [];
    }
  };

  const bulletRe = /^[\s]*(?:•|-|\*|\u2022|\d+[\.)])\s+(.+)$/;
  // "Label: value" with a short label (<30 chars) — avoids matching URLs.
  const kvRe = /^([A-Za-z][A-Za-z0-9 /&()+\-]{1,28})\s*[:：]\s*(.+)$/;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); flushBullets(); blocks.push({ kind: "spacer" }); continue; }
    const bm = raw.match(bulletRe);
    if (bm) { flushPara(); bulletBuf.push(bm[1].trim()); continue; }
    flushBullets();
    const km = line.match(kvRe);
    if (km) { flushPara(); blocks.push({ kind: "kv", label: km[1].trim(), value: km[2].trim() }); continue; }
    paraBuf.push(line);
  }
  flushPara();
  flushBullets();
  // Collapse consecutive spacers
  const out = [];
  for (const b of blocks) {
    if (b.kind === "spacer" && out.length && out[out.length - 1].kind === "spacer") continue;
    out.push(b);
  }
  return out;
}
